import { useQuery } from '@tanstack/react-query';
import { http } from '@/api/client/httpClient';
import { Spinner } from '@/components/common/Spinner';

// Enum display names (0-based, matching the C# enums).
const TYPE_OF_ENTITY = ['N/A', 'Investment Firm', 'Central Bank', 'EEA Credit Institution', 'Bank in third Country', 'Qualifying Market Fund', 'Payment Institution', 'Electronic Money Institution', 'Clearing House', 'Recognized Exchange', 'Other'];
const PRODUCT_TYPE = ['Derivatives Based', 'Cash Based'];
const PERMANENT_CAPITAL = ['150k', '750k'];
const PRUDENTIAL = ['Credit institution - CRR', 'IF Class 1B - CRR', 'IF Class 2 - DoA/underwriting - IFR', 'IF Class 2 - R&T - IFR', 'IF Class 3 - IFR', 'Alternative investment fund', 'UCITS and its management', 'MiFID PRU Investment Firm'];
const ACCOUNTING_FRAMEWORK = ['IFRS', 'UK Gaap'];
const LEVEL_OF_APPLICATION = ['Solo', 'Consolidated'];
const REASON_FOR_FALLING = ['N/A', 'Union Parent Investment Firm', 'Union Parent Investment Holding Company', 'Union Parent Mixed Financial Holding Company'];
const enumName = (arr: string[], v: number | null | undefined) => (v == null ? '' : arr[v] ?? String(v));

interface InfoDto { typeOfEntity?: number; maxCoverageOfFunds?: number; leiCode?: string; prudentialClassification?: number; permanentCapitalRequirement?: number; countryId?: number; timeZone?: string; balanceHaircutPercentage?: number; productType?: number }
interface SettingDto { ccyCurrencyId?: number; reportingCurrencyId?: number }
interface ReportsDto { trsIdentificationCodeOfEntity?: string; postalAddress?: string; faxNumber?: string; website?: string; icfMemberName?: string; cifLicenseNumber?: string; address?: string; street?: string; building?: string; district?: string; city?: string; tin?: string; inType?: string; reportingEntityCode?: string; accountingFramework?: number; levelOfApplication?: number; nameOfConsolidatingEntity?: string; reasonForFalling?: number }
interface ContactDto { contactFor?: number; email?: string }
interface CountryDto { id: number; mt45Name?: string }
interface CurrencyDto { id: number; name?: string }

function Field({ label, value, show = true }: { label: string; value: React.ReactNode; show?: boolean }) {
  if (!show) return null;
  return (
    <div className="col-md-4 col-lg-4 mb-2">
      <span className="fw-bold">{label}:</span> <span>{value}</span>
    </div>
  );
}

const has = (s?: string | null) => !!(s && String(s).trim());
const n0 = (v?: number | null) => (v == null ? '' : v.toLocaleString(undefined, { maximumFractionDigits: 0 }));

export function ProfileDetailsTab({ tenantId }: { tenantId: string }) {
  const opts = { enabled: !!tenantId, staleTime: 5 * 60_000 } as const;
  const info = useQuery<InfoDto>({ queryKey: ['tsetup', 'info', tenantId], queryFn: () => http.get(`/api/app/tenant-setup/info/${tenantId}`).then((r) => r.data), ...opts });
  const setting = useQuery<SettingDto>({ queryKey: ['tsetup', 'setting', tenantId], queryFn: () => http.get(`/api/app/tenant-setup/setting/${tenantId}`).then((r) => r.data), ...opts });
  const reports = useQuery<ReportsDto>({ queryKey: ['tsetup', 'reports', tenantId], queryFn: () => http.get(`/api/app/tenant-setup/reports/${tenantId}`).then((r) => r.data), ...opts });
  const contacts = useQuery<ContactDto[]>({ queryKey: ['tsetup', 'details', tenantId], queryFn: () => http.get(`/api/app/tenant-setup/details/${tenantId}`).then((r) => r.data), ...opts });
  const countries = useQuery<CountryDto[]>({ queryKey: ['tsetup', 'countries', tenantId], queryFn: () => http.get(`/api/app/country/countries/${tenantId}`).then((r) => r.data), ...opts });
  const currencies = useQuery<CurrencyDto[]>({ queryKey: ['tsetup', 'currencies', tenantId], queryFn: () => http.get(`/api/app/currency/currencies/${tenantId}`).then((r) => r.data), ...opts });

  if (info.isLoading || setting.isLoading || reports.isLoading) return <Spinner />;
  if (info.isError && setting.isError && reports.isError) {
    return <p className="text-muted">Profile details are unavailable.</p>;
  }

  const i = info.data ?? {};
  const s = setting.data ?? {};
  const r = reports.data ?? {};
  const notificationEmail = (contacts.data ?? []).find((c) => c.contactFor === 1)?.email ?? '';
  const countryName = (countries.data ?? []).find((c) => c.id === i.countryId)?.mt45Name ?? '';
  const ccyName = (currencies.data ?? []).find((c) => c.id === s.ccyCurrencyId)?.name ?? '';
  const reportingName = (currencies.data ?? []).find((c) => c.id === s.reportingCurrencyId)?.name ?? '';

  return (
    <div className="row g-3">
      <Field label="Notification Email" value={notificationEmail} show={has(notificationEmail)} />
      <Field label="Type Of Entity" value={enumName(TYPE_OF_ENTITY, i.typeOfEntity)} />
      <Field label="Maximum Coverage Of Funds In RC" value={n0(i.maxCoverageOfFunds)} />
      <Field label="LEI Code" value={i.leiCode} show={has(i.leiCode)} />
      <Field label="Prudential Classification" value={enumName(PRUDENTIAL, i.prudentialClassification)} />
      <Field label="Permanent Capital Requirement" value={enumName(PERMANENT_CAPITAL, i.permanentCapitalRequirement)} />
      <Field label="Country Of Incorporation" value={countryName} />
      <Field label="Time Zone" value={i.timeZone} show={has(i.timeZone)} />
      <Field label="CCY Currency" value={ccyName} />
      <Field label="Reporting Currency" value={reportingName} />
      <Field label="Balance Haircut Percentage" value={i.balanceHaircutPercentage ?? 0} />
      <Field label="Product Type" value={enumName(PRODUCT_TYPE, i.productType)} />

      <Field label="TRS IdentificationCode Of Entity" value={r.trsIdentificationCodeOfEntity} show={has(r.trsIdentificationCodeOfEntity)} />
      <Field label="Postal Address" value={r.postalAddress} show={has(r.postalAddress)} />
      <Field label="Fax Number" value={r.faxNumber} show={has(r.faxNumber)} />
      <Field label="Website" value={r.website} show={has(r.website)} />
      <Field label="ICF Member Name" value={r.icfMemberName} show={has(r.icfMemberName)} />
      <Field label="CIF License Number" value={r.cifLicenseNumber} show={has(r.cifLicenseNumber)} />
      <Field label="Address" value={r.address} show={has(r.address)} />
      <Field label="Street" value={r.street} show={has(r.street)} />
      <Field label="Building" value={r.building} show={has(r.building)} />
      <Field label="District Name" value={r.district} show={has(r.district)} />
      <Field label="City" value={r.city} show={has(r.city)} />
      <Field label="TIN" value={r.tin} show={has(r.tin)} />
      <Field label="INType" value={r.inType} show={has(r.inType)} />
      <Field label="Reporting Entity Code" value={r.reportingEntityCode} show={has(r.reportingEntityCode)} />
      <Field label="Accounting Framework" value={enumName(ACCOUNTING_FRAMEWORK, r.accountingFramework)} />
      <Field label="Level Of Application" value={enumName(LEVEL_OF_APPLICATION, r.levelOfApplication)} />
      <Field label="Name of Consolidating Entity" value={r.nameOfConsolidatingEntity} show={has(r.nameOfConsolidatingEntity)} />
      <Field label="Reason for falling under Consol Supervision" value={enumName(REASON_FOR_FALLING, r.reasonForFalling)} />
    </div>
  );
}
